/**
 * HTTP 请求封装
 * 基于 fetch API，提供与 axios 兼容的接口
 * 统一使用 src/config/index.js 中的 API_BASE_URL 变量
 */

import { Toast } from 'vant'
import router from '../router'
import { API_BASE_URL } from '../config'

// 需要重新登录的业务错误码
const AUTH_CODES = [1001, 1002, 1003, 1004]
let isAuthRedirecting = false

/**
 * 统一处理登录失效等认证错误
 * @param {object} json - 响应数据
 */
function handleAuthError(json) {
  if (isAuthRedirecting) return
  isAuthRedirecting = true
  Toast(json.msg || '登录已失效，请重新登录')
  localStorage.removeItem('token')
  localStorage.removeItem('userType')
  localStorage.removeItem('userInfo')
  if (router && router.push) {
    router.push('/login').catch(() => {})
  }
}

/**
 * 统一请求方法
 * @param {string} url - 请求地址
 * @param {object} data - 请求参数
 * @param {object} options - 额外配置 { headers, ... }
 * @returns {Promise<object>} 返回 res.data 结构
 */
function request(url, data = {}, options = {}) {
  const token = localStorage.getItem('token') || ''
  const userType = localStorage.getItem('userType') || 'personal'

  const defaultHeaders = {
    'Content-Type': 'application/json',
    'token': token,
    'User-Type': userType
  }

  // 合并 headers
  const headers = { ...defaultHeaders, ...(options.headers || {}) }

  // FormData 上传时不要手动设置 Content-Type，让浏览器自动加 boundary
  const isFormData = data instanceof FormData
  if (isFormData) {
    delete headers['Content-Type']
  }

  const config = {
    method: 'POST',
    headers,
    credentials: 'include',
    ...options
  }

  // 强制 POST 方法
  config.method = 'POST'

  if (isFormData) {
    config.body = data
    // 防止 options.headers 中的 Content-Type 覆盖默认删除逻辑
    if (config.headers) {
      delete config.headers['Content-Type']
    }
  } else {
    config.body = JSON.stringify(data)
  }

  return fetch(`${API_BASE_URL}${url}`, config)
    .then(async (response) => {
      // 非 2xx 状态码
      if (!response.ok) {
        let errorData = null
        try {
          errorData = await response.json()
        } catch (e) {
          errorData = { msg: response.statusText }
        }
        const error = new Error(
          (errorData && (errorData.msg || errorData.message)) ||
          `请求失败 (${response.status})`
        )
        error.response = { data: errorData, status: response.status }
        throw error
      }

      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        const json = await response.json()
        // 业务逻辑错误码处理
        if (json.code !== undefined && json.code !== 0 && json.code !== 200) {
          // 登录失效/未登录等认证错误，统一提示并跳转登录页
          if (AUTH_CODES.includes(json.code)) {
             const currentPath = window.location.pathname + window.location.search + window.location.hash
             if (!/^\/detail?newsCode=/.test(currentPath)) {
            handleAuthError(json)
            const error = new Error(json.msg || '登录已失效')
            error.response = { data: json }
            throw error
             }
          }
          const error = new Error(json.msg || json.message || '请求失败')
          error.response = { data: json }
          throw error
        }
        return json
      }

      // 非 JSON 响应
      return { data: await response.text() }
    })
    .catch((err) => {
      // 网络错误
      if (!err.response) {
        err.message = err.message || '网络异常，请检查网络连接'
      }
      throw err
    })
}

/**
 * request 对象，兼容 request.post(url, data, config) 调用方式
 */
const requestInstance = {
  post(url, data, config) {
    return request(url, data, config)
  },
  get(url, config) {
    return fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers: {
        'token': localStorage.getItem('token') || '',
        ...((config && config.headers) || {})
      },
      credentials: 'include',
      ...config
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`请求失败 (${response.status})`)
      }
      return response.json()
    })
  }
}

export default requestInstance
